import { forwardRef, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router";
import { kontaktModel } from "../../../api/models/kontaktModel";
import { useQueryWithPopupErrorHandling } from "../../../providers/utils/hooks";
import { useSelector } from "../../../redux/store";
import { KontaktSearchQuery } from "../../../ts/CP";
import { Kontakt } from "../../../ts/Kontakt";
import ReduxState from "../../../ts/Redux";
import Button from "../../ui/Button/Button";
import { LazyDataGrid } from "../../ui/LazyDataGrid/LazyDataGrid";
import { MultiLineCell } from "../../ui/LazyDataGrid/MultiLineCell";
import { ITEMS_PER_PAGE } from "../../ui/LazyDataGrid/common";
import { useKontakty } from "./KontaktyWrapper";
import "./SeznamKontaktu.scss";
import ExportButton from "../../../exports/ExportButton";

interface Props {
  setListShown(value: boolean): void;
  shown: boolean;
  query?: KontaktSearchQuery | null;
  setQuery?: (value: KontaktSearchQuery | null) => void;
  onSelectRow?: (row: Kontakt) => void;
}

const SeznamKontaktu = forwardRef(
  ({ shown, query, setQuery, onSelectRow }: Props, ref) => {
    const [, , setIndex] = useKontakty();
    const wrapperRef = useRef<HTMLDivElement>(null);

    const navigate = useNavigate();
    const { call } = useQueryWithPopupErrorHandling();
    const [data, setData] = useState<Kontakt[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(ITEMS_PER_PAGE);

    useEffect(() => {
      const getKontakty = async () => {
        setIsLoading(true);
        await call(
          () =>
            kontaktModel.getFilteredKontakty({
              offset: (page * pageSize).toString(),
              limit: pageSize.toString(),
              query: query ? JSON.stringify(query) : undefined,
            }),
          "Zobrazení kontaktov nebolo úspešné",
          null,
          ({ data, totalCount }) => {
            setData(data);
            setTotalCount(totalCount);
          }
        );
        setIsLoading(false);
      };

      getKontakty();
    }, [page, query, pageSize]);

    if (!setIndex) {
      return null;
    }

    return (
      <div
        style={{
          display: shown ? "block" : "none",
        }}
        ref={wrapperRef}
        className="kontakty-list"
      >
        <div className="kontakty-list-title">
          Kliknutím vyberiete kontakt
          {query && setQuery ? (
            <Button onClick={() => setQuery(null)}>VYMAZAT FILTR</Button>
          ) : null}
        </div>
        <LazyDataGrid<Kontakt>
          id="kontakty"
          rows={data}
          isLoading={isLoading}
          totalCount={totalCount}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onRowClick={(row) => {
            onSelectRow?.(row);
            navigate(`/kontakty/${row._id}`);
          }}
          columns={[
            {
              field: "id_klienta",
              headerName: "ID-KLIENT",
              width: 115,
            },
            {
              field: "klient",
              headerName: "KLIENT",
              renderCell: ({ row }) => (
                <MultiLineCell>{row.nazev ?? ""}</MultiLineCell>
              ),
              width: 200,
            },
            {
              field: "skupina",
              headerName: "SKUPINA",
              renderCell: ({ row }) => (
                <MultiLineCell>
                  {row.skupina?.nazev ?? row.skupina_name ?? ""}
                </MultiLineCell>
              ),
              width: 200,
            },
            {
              field: "jmeno",
              headerName: "MENO",
              width: 130,
            },
            {
              field: "prijmeni",
              headerName: "PRIEZVISKO",
              width: 130,
            },
            {
              field: "adresa",
              headerName: "ADRESA",
              width: 130,
              flex: 1,
            },
          ]}
        />
        <ExportButton
          type="kontakty"
          query={query ? JSON.stringify(query) : undefined}
        />
      </div>
    );
  }
);

const mapStateToProps = (state: ReduxState) => ({
  kontakty: state.kontakty,
  cs_skupiny: state.cs_skupiny,
});

export default connect(mapStateToProps, null, null, { forwardRef: true })(
  SeznamKontaktu
);
